import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  /** Accepted for backward compatibility but no longer emitted — Google ignores the meta keywords tag. */
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  noindex?: boolean;
}

/** Strip query string and hash so the canonical / og:url is a clean, indexable URL. */
function cleanUrl(raw: string): string {
  try {
    const u = new URL(raw);
    return u.origin + u.pathname;
  } catch {
    return raw;
  }
}

export function SEO({
  title = 'InterviewR - Expert Interview Preparation Platform',
  description = 'Connect with industry experts for mock interviews. Practice with real interviewers, get valuable feedback, and improve your interview skills. Join InterviewR today!',
  image = '/logo192.png',
  url = typeof window !== 'undefined' ? window.location.href : 'https://interview-r.com',
  type = 'website',
  noindex = false,
}: SEOProps) {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, attribute: string = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    updateMetaTag('description', description);
    updateMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');

    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://interview-r.com';
    const fullImageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;
    // Canonical / og:url must be the clean per-route URL (no query string or hash),
    // otherwise tracking params (?utm_source=…) fragment the URL into duplicates.
    const canonicalUrl = cleanUrl(url);

    // Open Graph tags
    updateMetaTag('og:title', title, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:image', fullImageUrl, 'property');
    updateMetaTag('og:url', canonicalUrl, 'property');
    updateMetaTag('og:type', type, 'property');
    updateMetaTag('og:site_name', 'InterviewR', 'property');

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', fullImageUrl);
    updateMetaTag('twitter:url', canonicalUrl);

    // Canonical URL — per-route, parameter-stripped, self-referencing
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);
  }, [title, description, image, url, type, noindex]);

  return null;
}
